import { Button, DatePicker, Form, Select, Space, Table, Tooltip, message, Modal, Descriptions, Divider, Tag } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import Loader from "components/common/Loader";
import CardsTable from "components/MovieListing/CardsTable";
import { CardStyle } from "components/style/componentsStyle";
import React from "react";
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { CSVLink } from "react-csv";
import { ROLES, STAGES, STAGES_LIST } from "components/common/Constants";
import SkeletonButton from "antd/lib/skeleton/Button";
import { getFormalName } from "components/services";
import moment from "moment";
import { roleSelector } from "features/jobinv/JobCardSlice";
import { useNavigate } from "react-router-dom";
import Skeleton from "components/common/Skeleton";
import ShowUpdateCount from "components/CardListing/ShowUpdateCount";
import { useGetClientsQuery } from "features/store";
import { useGetJobcardQuery } from "features/store";
import { arrayToQueryParam } from "components/CardListing/State";
import api from "utils/api";
import {
	setFromDate,
	setToDate,
	setJobIds,
	setClients,
	setStage,
	resetFilters,
	selectReportFilters,
} from "features/jobinv/reportSlice";

// Map frontend stage values to backend parameter names
const mapStageToBackendParam = (stage) => {
	if (!stage) return null;
	const stageMap = {
		creation: "creation",
		cutting: "cutting",
		secendory_process: "secendory",
		embroidary: "emboridary",
		production: "production",
		packing: "packing",
		account: "accountent",
		payment: "payment",
		dispatch: "dispatch",
		completed: "completed",
		designer: "designer",
		admin: "admin",
	};
	return stageMap[stage] || null;
};

// Get display name for stage filter dropdown
const getStageDisplayName = (stage) => {
	const stageDisplayMap = {
		creation: "Creation",
		cutting: "Cutting",
		secendory_process: "Secondary Process",
		embroidary: "Embroidary",
		production: "Production",
		packing: "Packing",
		account: "Billing",
		payment: "Payment",
		dispatch: "Dispatch",
		completed: "Completed",
		designer: "Designer",
		admin: "Admin",
	};
	return stageDisplayMap[stage] || getFormalName(stage) || stage;
};

const fetchJobcardsBetweenRangeAPI = async (
	from = null,
	to = null,
	jobid_filter = "",
	client_filter = "",
	stage_filter = null,
) => {
	// Only include dates if they are provided and not null
	let urlString = `/api/filterbydate`;
	const params = [];
	
	if (from && from !== "null") {
		params.push(`fromdate=${from}`);
	}
	if (to && to !== "null") {
		params.push(`enddate=${to}`);
	}
	if (jobid_filter) {
		params.push(`jobid=${jobid_filter}`);
	}
	if (client_filter) {
		params.push(client_filter.replace("?", ""));
	}
	if (stage_filter) {
		const backendStageParam = mapStageToBackendParam(stage_filter);
		if (backendStageParam) {
			params.push(`${backendStageParam}=true`);
		}
	}
	
	if (params.length > 0) {
		urlString += "?" + params.join("&");
	}
	
	const response = await api.get(urlString);
	return response.data;
};

function JobCardBetweenDates(props) {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const role = useSelector(roleSelector);
	const { fromDate, toDate, jobIds, clients: selectedClients, stage: selectedStage } = useSelector(selectReportFilters);
	const [cards, setCards] = React.useState([]);
	const [tableCards, setTableCards] = React.useState([]);
	const [loading, setLoading] = React.useState(false);
	const [tableColumns, setTableColumns] = React.useState();
	const [form] = Form.useForm();
	const { data: clients, isLoading } = useGetClientsQuery();
	const csvLinkRef = React.useRef(null);

	// Info modal state for packing/dispatch details
	const [infoModal, setInfoModal] = React.useState({ visible: false, jobcardId: null });
	const { data: jobcardDetail, isLoading: isJobcardDetailLoading } = useGetJobcardQuery(infoModal.jobcardId, { skip: !infoModal.jobcardId });

	const fetchCards = async (from, to, jobid_filter, client_filter, stage_filter) => {
		setLoading(true);
		try {
			const cards = await fetchJobcardsBetweenRangeAPI(from, to, jobid_filter, client_filter, stage_filter);
			setCards(cards || []);
		} catch (error) {
			const status = error?.response?.status;
			const detail = error?.response?.data?.detail;
			if (status === 401) {
				message.error("Session expired. Please try again.");
			} else {
				message.error(detail || "Failed to fetch job cards between the dates.");
			}
		} finally {
			setLoading(false);
		}
	};

	const handleFinish = (values) => {
		const from = values.from ? values.from.format("YYYY-MM-DD") : fromDate;
		const to = values.to ? values.to.format("YYYY-MM-DD") : toDate;
		const stage = values.stage !== undefined ? values.stage : selectedStage;
		
		// Validate: If stage is selected, dates are required
		if (stage && (!from || !to)) {
			alert("Please select both From Date and To Date when using Stage filter.");
			return;
		}
		
		const jobidQuery = arrayToQueryParam("jobid", jobIds);
		const clientQuery = arrayToQueryParam("client", selectedClients);
		dispatch(setFromDate(from));
		dispatch(setToDate(to));
		dispatch(setStage(stage || null));
		fetchCards(from, to, jobidQuery ? jobidQuery.split("=")[1] : "", clientQuery, stage || null);
	};

	const handleReset = () => {
		dispatch(resetFilters());
		form.resetFields();
		setCards([]);
	};

	const handleExportToExcel = () => {
		// Check if there's data to export
		if (!dataSource || dataSource.length === 0) {
			alert("No data to export. Please search for job cards first.");
			return;
		}
		// Trigger the CSV download
		if (csvLinkRef.current) {
			// react-csv exposes the link element through the ref
			const linkElement = csvLinkRef.current.link || csvLinkRef.current;
			if (linkElement && typeof linkElement.click === 'function') {
				linkElement.click();
			}
		}
	};

	const dataSource =
		clients?.length > 0
			? cards.map((card) => ({
					...card,
					client_company: clients?.find((client) => client.id === card.client)
						?.company_name,
					client_phone: clients?.find((client) => client.id === card.client)?.phone,
					current_stage:
						card.current_stage == null 
							? (card.is_degitizer_stage ? null : STAGES.CREATION)
							: card.current_stage,
			  }))
			: cards;

	// Prepare CSV data with all columns from the table
	const prepareCSVData = () => {
		if (!dataSource || dataSource.length === 0) return [];
		
		return dataSource.map((card) => {
			const formattedStatus = card.status
				? card.status
						.split('_')
						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
						.join(' ')
				: 'Pending';
			
			// Format stage date/time columns
			const formatStageDateTime = (date, time) => {
				if (!date && !time) return "";
				const formattedTime = time ? moment(time, "HH:mm:ss").format("HH:mm") : "";
				return date && formattedTime ? `${date} ${formattedTime}` : date || formattedTime;
			};

			const client = clients?.find((c) => c.id === card.client);
			
			return {
				"Job Card No.": card.serial_number || card.id,
				"Client Company": card.client_company || client?.company_name || "",
				"Date": card.date || "",
				"Deliver Date": card.deliver_date || "",
				"Quantity": card.quantity || 0,
				"Created By": card.Created_by || "",
				"Current Stage": card.current_stage 
					? getFormalName(card.current_stage) 
					: (card.is_degitizer_stage ? "Revision" : ""),
				"Payment Status": formattedStatus,
				"Designer": formatStageDateTime(card.designer_update_date, card.designer_update_time),
				"Cutting": formatStageDateTime(card.cutting_update_date, card.cutting_update_time),
				"Secondary Process": formatStageDateTime(card.secendory_update_date, card.secendory_update_time),
				"Embroidary": formatStageDateTime(card.emboridary_update_date, card.emboridary_update_time),
				"Production": formatStageDateTime(card.production_update_date, card.production_update_time),
				"Packing": formatStageDateTime(card.packing_update_date, card.packing_update_time),
				"Billing": formatStageDateTime(card.accountent_update_date, card.accountent_update_time),
				"Payment": formatStageDateTime(card.payment_update_date, card.payment_update_time),
				"Dispatch": formatStageDateTime(card.dispatch_update_date, card.dispatch_update_time),
				"Del.Address": card.shipping_address || "",
				"Co.Mode": card.courier_mode || "",
				"Customer Name": client?.company_name || "",
				"Cash": client?.cash || "",
				"GST": client?.GST_NO || "",
				"Rate": card.rate || "",
				"Style": card.style || "",
			};
		});
	};

	const csvHeaders = [
		{ label: "Job Card No.", key: "Job Card No." },
		{ label: "Client Company", key: "Client Company" },
		{ label: "Date", key: "Date" },
		{ label: "Deliver Date", key: "Deliver Date" },
		{ label: "Quantity", key: "Quantity" },
		{ label: "Created By", key: "Created By" },
		{ label: "Current Stage", key: "Current Stage" },
		{ label: "Payment Status", key: "Payment Status" },
		{ label: "Designer", key: "Designer" },
		{ label: "Cutting", key: "Cutting" },
		{ label: "Secondary Process", key: "Secondary Process" },
		{ label: "Embroidary", key: "Embroidary" },
		{ label: "Production", key: "Production" },
		{ label: "Packing", key: "Packing" },
		{ label: "Billing", key: "Billing" },
		{ label: "Payment", key: "Payment" },
		{ label: "Dispatch", key: "Dispatch" },
		{ label: "Del.Address", key: "Del.Address" },
		{ label: "Co.Mode", key: "Co.Mode" },
		{ label: "Customer Name", key: "Customer Name" },
		{ label: "Cash", key: "Cash" },
		{ label: "GST", key: "GST" },
		{ label: "Rate", key: "Rate" },
		{ label: "Style", key: "Style" },
	];

	const underProcessCards = tableCards.filter((card) => card.current_stage !== STAGES.COMPLETED);

	const columns = [
		{
			title: "Job Card No.",
			dataIndex: "serial_number",
			key: "serial_number",
			render: (text, record) => record.serial_number || record.id,
			sorter: (a, b) => parseInt(a.id) - parseInt(b.id),
			width: 100
		},
		{
			title: "Client Company",
			dataIndex: "client_company",
			key: "client_company",
			filters: clients?.map((client) => ({
				text: client.company_name,
				value: client.company_name,
			})),
			onFilter: (value, record) => record.client_company.indexOf(value) === 0,
			render: (text, record) => (
				<Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/detail/${record.id}`)}>
					{text}
				</Button>
			),
		},
		{
			title: "Date",
			dataIndex: "date",
			key: "date",
			sorter: (a, b) =>
				moment(a.date, "DD/MM/YYYY").unix() - moment(b.date, "DD/MM/YYYY").unix(),
		},
		{
			title: "Deliver Date",
			dataIndex: "deliver_date",
			key: "deliver_date",
			sorter: (a, b) =>
				moment(a.deliver_date, "DD/MM/YYYY").unix() -
				moment(b.deliver_date, "DD/MM/YYYY").unix(),
		},
		{
			title: "Quantity",
			dataIndex: "quantity",
			key: "quantity",
		},
		{
			title: "Created By",
			dataIndex: "Created_by",
			key: "created_by",
		},
		{
			title: "Current Stage",
			dataIndex: "current_stage",
			key: "current_stage",
			filters: STAGES_LIST.map((stage) => ({
				text: getFormalName(stage),
				value: stage,
			})),
			onFilter: (value, record) => record.current_stage.indexOf(value) === 0,
			render: (text, record) => {
				return getFormalName(text);
			},
		},
		{
			title: "Payment Status",
			dataIndex: "status",
			key: "status",
			render: (text, record) => {
				// Get status from record (which comes from API response)
				const status = record.status || text || "pending";
				// Convert snake_case to Title Case
				const formattedStatus = status
					.split('_')
					.map(word => word.charAt(0).toUpperCase() + word.slice(1))
					.join(' ');
				return formattedStatus;
			},
		},
		{
			title: "Designer",
			dataIndex: "designer_update_date",
			key: "designer_update_date",
			render: (date, record) => {
				const time = record.designer_update_time;
				const formattedTime = moment(time, "HH:mm:ss").format("HH:mm");
				return (
					(!date && !time) ? "" : <div style={{display: "flex", flexDirection: "column"}}>
					<span>{date}</span><span>{formattedTime}</span>
				</div>
				);
			},
		},
		{
			title: "Cutting",
			dataIndex: "cutting_update_date",
			key: "cutting_update_date",
			render: (date, record) => {
				const time = record.cutting_update_time;
				const formattedTime = time ? moment(time, "HH:mm:ss").format("HH:mm") : "";
				return !date && !time ? (
					""
				) : (
					<div style={{ display: "flex", flexDirection: "column" }}>
						<span>{date}</span>
						<span>{formattedTime}</span>
					</div>
				);
			},
		},
		{
			title: "Secondary Process",
			dataIndex: "secendory_update_date",
			key: "secendory_update_date",
			render: (date, record) => {
				const time = record.secendory_update_time;
				const formattedTime = time ? moment(time, "HH:mm:ss").format("HH:mm") : "";
				return !date && !time ? (
					""
				) : (
					<div style={{ display: "flex", flexDirection: "column" }}>
						<span>{date}</span>
						<span>{formattedTime}</span>
					</div>
				);
			},
		},
		{
			title: "Embroidary",
			dataIndex: "emboridary_update_date",
			key: "emboridary_update_date",
			render: (date, record) => {
				const time = record.emboridary_update_time;
				const formattedTime = time ? moment(time, "HH:mm:ss").format("HH:mm") : "";
				return !date && !time ? (
					""
				) : (
					<div style={{ display: "flex", flexDirection: "column" }}>
						<span>{date}</span>
						<span>{formattedTime}</span>
					</div>
				);
			},
		},
		{
			title: "Production",
			dataIndex: "production_update_date",
			key: "production_update_date",
			render: (date, record) => {
				const time = record.production_update_time;
				const formattedTime = time ? moment(time, "HH:mm:ss").format("HH:mm") : "";
				return !date && !time ? (
					""
				) : (
					<div style={{ display: "flex", flexDirection: "column" }}>
						<span>{date}</span>
						<span>{formattedTime}</span>
					</div>
				);
			},
		},
		{
			title: "Packing",
			dataIndex: "packing_update_date",
			key: "packing_update_date",
			render: (date, record) => {
				const time = record.packing_update_time;
				const formattedTime = time ? moment(time, "HH:mm:ss").format("HH:mm") : "";
				return !date && !time ? (
					""
				) : (
					<div style={{ display: "flex", flexDirection: "column" }}>
						<span>{date}</span>
						<span>{formattedTime}</span>
					</div>
				);
			},
		},
		{
			title: "Billing",
			dataIndex: "accountent_update_date",
			key: "accountent_update_date",
			render: (date, record) => {
				const time = record.accountent_update_time;
				const formattedTime = time ? moment(time, "HH:mm:ss").format("HH:mm") : "";
				return (
					(!date && !time) ? "" : <div style={{display: "flex", flexDirection: "column"}}>
					<span>{date}</span><span>{formattedTime}</span>
				</div>
				);
			},
		},
		{
			title: "Payment",
			dataIndex: "payment_update_date",
			key: "payment_update_date",
			render: (date, record) => {
				const time = record.payment_update_time;
				const formattedTime = time ? moment(time, "HH:mm:ss").format("HH:mm") : "";
				return (
					(!date && !time) ? "" : <div style={{display: "flex", flexDirection: "column"}}>
					<span>{date}</span><span>{formattedTime}</span>
				</div>
				);
			},
		},
		{
			title: "Dispatch",
			dataIndex: "dispatch_update_date",
			key: "dispatch_update_date",
			render: (date, record) => {
				const time = record.dispatch_update_time;
				const formattedTime = time ? moment(time, "HH:mm:ss").format("HH:mm") : "";
				return (
					(!date && !time) ? "" : <div style={{display: "flex", flexDirection: "column"}}>
					<span>{date}</span><span>{formattedTime}</span>
				</div>
				);
			},
		},
		{
			title: "Del.Address",
			dataIndex: "shipping_address",
			key: "shipping_address",
			render: (text) => text || "",
		},
		{
			title: "Co.Mode",
			dataIndex: "courier_mode",
			key: "courier_mode",
			render: (text, record) => (
				<Button type="link" style={{ padding: 0 }} onClick={() => setInfoModal({ visible: true, jobcardId: record.id })}>
					{record.courier_mode || "—"}
				</Button>
			),
		},
	];

	// Map selected stage to column title
	const getStageColumnTitle = (stage) => {
		const stageColumnMap = {
			creation: null, // Creation doesn't have a separate column
			designer: "Designer",
			cutting: "Cutting",
			secendory_process: "Secondary Process",
			embroidary: "Embroidary",
			production: "Production",
			packing: "Packing",
			account: "Billing",
			payment: "Payment",
			dispatch: "Dispatch",
			completed: null, // Completed is a terminal stage; no separate column
			admin: null, // Admin doesn't have a separate column
		};
		return stageColumnMap[stage] || null;
	};

	useEffect(() => {
		let _table_columns = columns;

		// Filter stage columns based on selected stage
		if (selectedStage) {
			const stageColumnTitle = getStageColumnTitle(selectedStage);
			if (stageColumnTitle) {
				// Only show the selected stage column, hide all other stage columns
				_table_columns = columns.filter((column) => {
					// Keep non-stage columns
					const stageColumns = [
						"Designer",
						"Cutting",
						"Secondary Process",
						"Embroidary",
						"Production",
						"Packing",
						"Billing",
						"Payment",
						"Dispatch",
					];
					if (!stageColumns.includes(column.title)) {
						return true; // Keep non-stage columns
					}
					// Only keep the selected stage column
					return column.title === stageColumnTitle;
				});
			} else {
				// For stages without specific columns (creation, admin), hide all stage columns
				const stageColumns = [
					"Designer",
					"Cutting",
					"Secondary Process",
					"Embroidary",
					"Production",
					"Packing",
					"Billing",
					"Payment",
					"Dispatch",
				];
				_table_columns = columns.filter((column) => !stageColumns.includes(column.title));
			}
		}

		if (
			[
				ROLES.CUTTING,
				ROLES.PRODUCTION,
				ROLES.SECONDARY_PROCESS,
				ROLES.EMBROIDARY,
				ROLES.ACCOUNT,
			].includes(role)
		) {
			if (role === ROLES.ACCOUNT) {
				setTableColumns(_table_columns.filter((column) => column.title !== "Update"));
			} else {
				setTableColumns(
					_table_columns
						.filter((column) => column.title !== "Update")
						.filter((column) => column.title !== "Client Company"),
				);
			}
		} else {
			setTableColumns(_table_columns);
		}

		if (fromDate && toDate) {
			const jobidQuery = arrayToQueryParam("jobid", jobIds);
			const clientQuery = arrayToQueryParam("client", selectedClients);
			fetchCards(fromDate, toDate, jobidQuery ? jobidQuery.split("=")[1] : "", clientQuery, selectedStage);
		}
	}, [role, fromDate, toDate, selectedStage]);

	useEffect(() => {
		form.setFieldsValue({
			from: fromDate ? moment(fromDate, "YYYY-MM-DD") : null,
			to: toDate ? moment(toDate, "YYYY-MM-DD") : null,
			jobids: jobIds.map(String),
			clients: selectedClients,
			stage: selectedStage,
		});
	}, [fromDate, toDate, jobIds, selectedClients, selectedStage, form]);

	return (
		<div>
			<CardStyle>
				<div className="title">Report</div>
				<Style>
					<div className="top">
						<Form layout="inline" onFinish={handleFinish} form={form} style={{ width: "100%", display: "flex", flexWrap: "nowrap", alignItems: "center", gap: "4px", overflowX: "auto" }}>
							<Form.Item label="From Date" name="from" style={{ marginBottom: 0, flexShrink: 0 }}>
								<DatePicker format={"DD/MM/YYYY"} style={{ width: 140 }} />
							</Form.Item>
							<Form.Item label="To Date" name="to" style={{ marginBottom: 0, flexShrink: 0 }}>
								<DatePicker format={"DD/MM/YYYY"} style={{ width: 140 }} />
							</Form.Item>
							<Form.Item label="Job Card No." name="jobids" style={{ marginBottom: 0, flexShrink: 0 }}>
								<Select
									mode="tags"
									placeholder="Job Card No.or Serial No."
									value={jobIds.map(String)}
									onChange={(values) => {
										const uniqueJobIds = [
											...new Set(
												values
													.map((v) => String(v).trim())
													.filter((v) => v.length > 0),
											),
										];
										dispatch(setJobIds(uniqueJobIds));
									}}
									tokenSeparators={[","]}
									style={{ width: 150 }}
								/>
							</Form.Item>
							<Form.Item label="Clients" name="clients" style={{ marginBottom: 0, flexShrink: 0 }}>
								<Select
									mode="multiple"
									placeholder="Select Client"
									value={selectedClients}
									onChange={(values) => dispatch(setClients(values))}
									style={{ width: 150 }}
									options={clients?.map((item) => ({
										value: item.id,
										label: item.company_name,
									}))}
									filterOption={(inputValue, option) =>
										option.label
											.toLowerCase()
											.indexOf(inputValue.toLowerCase()) >= 0
									}
								/>
							</Form.Item>
							{[ROLES.ADMIN, ROLES.DESIGN, ROLES.ACCOUNT].includes(role) && (
								<Form.Item label="Stage" name="stage" style={{ marginBottom: 0, flexShrink: 0 }}>
									<Select
										placeholder="Select Stage"
										value={selectedStage}
										onChange={(value) => dispatch(setStage(value || null))}
										style={{ width: 150 }}
										allowClear
										options={[
											{ value: "creation", label: getStageDisplayName("creation") },
											{ value: "designer", label: getStageDisplayName("designer") },
											{ value: "cutting", label: getStageDisplayName("cutting") },
											{ value: "secendory_process", label: getStageDisplayName("secendory_process") },
											{ value: "embroidary", label: getStageDisplayName("embroidary") },
											{ value: "production", label: getStageDisplayName("production") },
											{ value: "packing", label: getStageDisplayName("packing") },
											{ value: "account", label: getStageDisplayName("account") },
											{ value: "payment", label: getStageDisplayName("payment") },
											{ value: "dispatch", label: getStageDisplayName("dispatch") },
											{ value: "completed", label: getStageDisplayName("completed") },
											{ value: "admin", label: getStageDisplayName("admin") },
										]}
									/>
								</Form.Item>
							)}
							<Form.Item style={{ marginLeft: "auto", marginBottom: 0, flexShrink: 0 }}>
								<Space size="small">
									<Button type="primary" htmlType="submit" size="medium">
										Search
									</Button>
									<Button onClick={handleReset} size="medium">
										Reset
									</Button>
									<Tooltip title="Export To Excel">
										<Button 
											icon={<FileExcelOutlined />} 
											size="medium"
											onClick={handleExportToExcel}
										>
										</Button>
									</Tooltip>
									<CSVLink
										ref={csvLinkRef}
										data={prepareCSVData()}
										headers={csvHeaders}
										filename={`versatileapparels_${fromDate || 'all'}_${toDate || 'all'}.csv`}
										style={{ display: 'none' }}
									/>
								</Space>
							</Form.Item>
						</Form>
					</div>

					{clients && !loading ? (
						<>
							<ShowUpdateCount cards={cards} selectedStage={selectedStage} />
							<Table
								style={{ width: "100%" }}
								dataSource={dataSource}
								columns={tableColumns}
								rowClassName="table-row"
								scroll={{ x: "max-content" }}

							/>
							<Modal
								title={`Jobcard #${jobcardDetail?.serial_number || infoModal.jobcardId || ""} - Summary`}
								open={infoModal.visible}
								onCancel={() => setInfoModal({ visible: false, jobcardId: null })}
								footer={[
									<Button key="close" onClick={() => setInfoModal({ visible: false, jobcardId: null })}>
										Close
									</Button>,
									<Button
										key="open"
										type="primary"
										onClick={() => {
											const id = infoModal.jobcardId;
											setInfoModal({ visible: false, jobcardId: null });
											navigate(`/detail/${id}`);
										}}
									>
										Open Full Page
									</Button>,
								]}
								width={700}
							>
								{!jobcardDetail ? (
									<Loader />
								) : (
									<div style={{ paddingTop: 8 }}>
										<Descriptions bordered column={1} size="middle">
											<Descriptions.Item label="Client">
												{clients?.find((c) => c.id === jobcardDetail.client)?.company_name || "—"}
											</Descriptions.Item>
											<Descriptions.Item label="Quantity">
												{jobcardDetail.quantity ?? "—"}
											</Descriptions.Item>
											<Descriptions.Item label="Delivery Date">
												{jobcardDetail.deliver_date || "—"}
											</Descriptions.Item>
											<Descriptions.Item label="Current Stage">
												<Tag color="blue">{getFormalName(jobcardDetail.current_stage) || "—"}</Tag>
											</Descriptions.Item>
										</Descriptions>

										<Divider />

										<Descriptions title="Dispatch / Packing" bordered column={1} size="middle">
											<Descriptions.Item label="Courier Mode">
												{jobcardDetail.courier_mode || "—"}
												{jobcardDetail.courier_mode === "Courier" && jobcardDetail.courier_name
													? ` — ${jobcardDetail.courier_name}`
													: ""}
											</Descriptions.Item>
											<Descriptions.Item label="Shipping Address">
												{jobcardDetail.shipping_address || "—"}
											</Descriptions.Item>
											<Descriptions.Item label="Carton Boxes">
												{Array.isArray(jobcardDetail.carton_boxes) && jobcardDetail.carton_boxes.length > 0 ? (
													<div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
														{jobcardDetail.carton_boxes.map((box, idx) => (
															<Tag key={`${box.size}-${box.quantity}-${idx}`} color="geekblue">
																Size {box.size}: {box.quantity}
															</Tag>
														))}
													</div>
												) : (
													jobcardDetail.carton || "—"
												)}
											</Descriptions.Item>
											<Descriptions.Item label="Packing Note">
												{jobcardDetail.packing_note || "—"}
											</Descriptions.Item>
										</Descriptions>
									</div>
								)}
							</Modal>
						</>
					) : (
						<Skeleton times={3} />
					)}
				</Style>
			</CardStyle>
		</div>
	);
}

const Style = styled.div`
	width: 100%;
	.skeleton-with-100 {
		width: 100%;
	}
	.top {
		display: flex;
		padding: 2rem 0;
	}
	.title {
		font-size: 1.8rem;
		font-weight: bold;
	}
`;

export default JobCardBetweenDates;
